/*******************************************************************************
 * Copyright (c) 2024-2026 Malond Limited. All rights reserved.
 * The source code is the property of Malond Limited.
 ******************************************************************************/

import useSWR from "swr"
import { useRouter } from "next/router"
import { useAuthReady } from "./useAuthReady"
import {
  UrlOrderFindListAPI,
  UrlOrderGetAllSumInShopAPI,
  UrlOrderGetAllSumInShopPaymentPointAPI,
  UrlOrderGetAllSumInShopPaymentPointsAPI,
  UrlOrderGetAllSumInShopSalePointAPI,
  UrlOrderGetAllSumInShopSalePointsAPI,
  UrlOrderGetAllSumInShopsAPI,
  UrlOrderGetOneAPI,
} from "../libs/api/url"
import {
  Order,
  ResponseOrderGetList,
  ResponseOrderGetOne,
  ResponseOrderGetSums,
} from "../libs/api/backend/src"

export function useOrderId(): string {
  let orderId = ""

  try {
    const router = useRouter()
    orderId = (router.query.orderId as string) || ""
  } catch (err) {
    console.error(err)
  }

  return orderId
}

export function useOrderFindOne(enterpriseId?: string, orderId?: string) {
  const { isAuthenticated, authLoading } = useAuthReady()
  const { data: apiResponse, error } = useSWR<ResponseOrderGetOne>(
    isAuthenticated && enterpriseId && orderId
      ? UrlOrderGetOneAPI(enterpriseId, orderId)
      : null
  )

  const order = apiResponse && (apiResponse.data as Order)

  return {
    order: apiResponse && order ? order : null,
    isLoading: Boolean(
      authLoading ||
      (isAuthenticated && enterpriseId && orderId && !error && !apiResponse)
    ),
    isError:
      error ||
      (apiResponse && !apiResponse.success
        ? apiResponse.errorMessage
        : undefined),
  }
}

export function useOrderFindList(
  enterpriseId?: string,
  {
    shopId,
    shopSalePointId,
    shopPaymentPointId,
    userId,
    limit,
    offset,
  }: {
    shopId?: string
    shopSalePointId?: string
    shopPaymentPointId?: string
    userId?: string
    limit?: number
    offset?: number
  } = {}
) {
  const { isAuthenticated, authLoading } = useAuthReady()
  const { data: apiResponse, error } = useSWR<ResponseOrderGetList>(
    isAuthenticated && enterpriseId
      ? UrlOrderFindListAPI(enterpriseId, {
          shopId,
          shopSalePointId,
          shopPaymentPointId,
          userId,
          limit,
          offset,
        })
      : null
  )

  const orders = apiResponse && (apiResponse.data as Array<Order>)
  const meta = apiResponse && apiResponse.meta

  return {
    orders: apiResponse?.data ?? null,
    meta: apiResponse && meta ? meta : undefined,
    isLoading: Boolean(
      authLoading || (isAuthenticated && enterpriseId && !error && !apiResponse)
    ),
    isError:
      error ||
      (apiResponse && !apiResponse.success
        ? apiResponse.errorMessage
        : undefined),
  }
}

export function useOrderGetAllSumInShops(enterpriseId?: string) {
  const { isAuthenticated, authLoading } = useAuthReady()
  const { data: apiResponse, error } = useSWR<ResponseOrderGetSums>(
    isAuthenticated && enterpriseId
      ? UrlOrderGetAllSumInShopsAPI(enterpriseId)
      : null
  )

  const totalSums = apiResponse && apiResponse.data

  return {
    total: apiResponse && totalSums ? totalSums : null,
    isLoading: Boolean(
      authLoading || (isAuthenticated && enterpriseId && !error && !apiResponse)
    ),
    isError:
      error ||
      (apiResponse && !apiResponse.success
        ? apiResponse.errorMessage
        : undefined),
  }
}

export function useOrderGetAllSumInShop(
  enterpriseId?: string,
  shopId?: string
) {
  const { isAuthenticated, authLoading } = useAuthReady()
  const { data: apiResponse, error } = useSWR<ResponseOrderGetSums>(
    isAuthenticated && enterpriseId && shopId
      ? UrlOrderGetAllSumInShopAPI(enterpriseId, shopId)
      : null
  )

  const totalSums = apiResponse && apiResponse.data

  return {
    total: apiResponse && totalSums ? totalSums : null,
    isLoading: Boolean(
      authLoading ||
      (isAuthenticated && enterpriseId && shopId && !error && !apiResponse)
    ),
    isError:
      error ||
      (apiResponse && !apiResponse.success
        ? apiResponse.errorMessage
        : undefined),
  }
}

export function useOrderGetAllSumInShopSalePoints(
  enterpriseId?: string,
  shopId?: string
) {
  const { isAuthenticated, authLoading } = useAuthReady()
  const { data: apiResponse, error } = useSWR<ResponseOrderGetSums>(
    isAuthenticated && enterpriseId && shopId
      ? UrlOrderGetAllSumInShopSalePointsAPI(enterpriseId, shopId)
      : null
  )

  const totalSums = apiResponse && apiResponse.data

  return {
    total: apiResponse && totalSums ? totalSums : null,
    isLoading: Boolean(
      authLoading ||
      (isAuthenticated && enterpriseId && shopId && !error && !apiResponse)
    ),
    isError:
      error ||
      (apiResponse && !apiResponse.success
        ? apiResponse.errorMessage
        : undefined),
  }
}

export function useOrderGetAllSumInShopSalePoint(
  enterpriseId?: string,
  shopId?: string,
  shopSalePointId?: string
) {
  const { isAuthenticated, authLoading } = useAuthReady()
  const { data: apiResponse, error } = useSWR<ResponseOrderGetSums>(
    isAuthenticated && enterpriseId && shopId && shopSalePointId
      ? UrlOrderGetAllSumInShopSalePointAPI(
          enterpriseId,
          shopId,
          shopSalePointId
        )
      : null
  )

  const totalSums = apiResponse && apiResponse.data

  return {
    total: apiResponse && totalSums ? totalSums : null,
    isLoading: Boolean(
      authLoading ||
      (isAuthenticated &&
        enterpriseId &&
        shopId &&
        shopSalePointId &&
        !error &&
        !apiResponse)
    ),
    isError:
      error ||
      (apiResponse && !apiResponse.success
        ? apiResponse.errorMessage
        : undefined),
  }
}

export function useOrderGetAllSumInShopPaymentPoints(
  enterpriseId?: string,
  shopId?: string,
  shopSalePointId?: string
) {
  const { isAuthenticated, authLoading } = useAuthReady()
  const { data: apiResponse, error } = useSWR<ResponseOrderGetSums>(
    isAuthenticated && enterpriseId && shopId && shopSalePointId
      ? UrlOrderGetAllSumInShopPaymentPointsAPI(
          enterpriseId,
          shopId,
          shopSalePointId
        )
      : null
  )

  const totalSums = apiResponse && apiResponse.data

  return {
    total: apiResponse && totalSums ? totalSums : null,
    isLoading: Boolean(
      authLoading ||
      (isAuthenticated &&
        enterpriseId &&
        shopId &&
        shopSalePointId &&
        !error &&
        !apiResponse)
    ),
    isError:
      error ||
      (apiResponse && !apiResponse.success
        ? apiResponse.errorMessage
        : undefined),
  }
}

export function useOrderGetAllSumInShopPaymentPoint(
  enterpriseId?: string,
  shopId?: string,
  shopSalePointId?: string,
  shopPaymentPointId?: string
) {
  const { isAuthenticated, authLoading } = useAuthReady()
  const { data: apiResponse, error } = useSWR<ResponseOrderGetSums>(
    isAuthenticated &&
      enterpriseId &&
      shopId &&
      shopSalePointId &&
      shopPaymentPointId
      ? UrlOrderGetAllSumInShopPaymentPointAPI(
          enterpriseId,
          shopId,
          shopSalePointId,
          shopPaymentPointId
        )
      : null
  )

  const totalSums = apiResponse && apiResponse.data

  return {
    total: apiResponse && totalSums ? totalSums : null,
    isLoading: Boolean(
      authLoading ||
      (isAuthenticated &&
        enterpriseId &&
        shopId &&
        shopSalePointId &&
        shopPaymentPointId &&
        !error &&
        !apiResponse)
    ),
    isError:
      error ||
      (apiResponse && !apiResponse.success
        ? apiResponse.errorMessage
        : undefined),
  }
}
